"use client";

import { useRef, useState, useEffect } from "react";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import SponsorLogos from "@/components/home/sponsor-logos/sponsor-logos";
import Recognition from "@/components/home/recognition/recognition";
import OurServices from "@/components/home/our-services/OurServices";
import { Fraunces } from "next/font/google";
import styles from "./home-page/home-page.module.scss";
import Footer from "@/components/footer/footer";
import WelcomePopup from "@/components/home/welcome-popup/WelcomePopup";
import { useAuth } from "@/providers/auth-context";

const fraunces = Fraunces({ subsets: ["latin"], weight: ["400", "600"], variable: "--font-fraunces" });

function DarkenText({ children }: { children: React.ReactNode }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.9", "end 0.3"],
  });
  const color = useTransform(scrollYProgress, [0, 1], ["#c8c5be", "#151515"]);
  return (
    <motion.span ref={ref} style={{ color }}>
      {children}
    </motion.span>
  );
}

const HeroMap = dynamic(() => import("./home-page/HeroMap"), {
  ssr: false,
  loading: () => (
    <div className={styles.mapLoading}>Loading map…</div>
  ),
});


function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0 });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay }}
    >
      {children}
    </motion.div>
  );
}

function Counter({ value, suffix = "" }: { value: number; suffix?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let current = 0;
    const duration = 1600;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      current += increment;
      if (current >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.round(current));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return <span ref={ref}>{display}{suffix}</span>;
}

function AccordionItem({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.accordionItem}>
      <button className={styles.accordionHeader} onClick={() => setOpen(!open)}>
        <div className={styles.accordionLeft}>
          <div className={styles.accordionIconWrap}>{icon}</div>
          <span className={styles.accordionTitle}>{title}</span>
        </div>
        <span className={styles.accordionPlus}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className={styles.accordionBody}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const { authenticated } = useAuth();
  return (
    <div className={styles.page}>
      <WelcomePopup />

      <section className={styles.hero}>
        <motion.div
          className={styles.heroBrand}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.0 }}
        >
          <Image
            src="/logo_white.svg"
            alt="AI2Peat logo"
            width={64}
            height={64}
            className={styles.heroBrandLogo}
          />
          <span className={styles.heroBrandName}>AI2Peat</span>
        </motion.div>

        <motion.div
          className={styles.heroChips}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.1 }}
        >
          <span className={styles.heroChip}>Earth Observation</span>
          <span className={styles.heroChip}>AI Native</span>
          <span className={styles.heroChip}>Ecological Insights</span>
        </motion.div>

        <motion.h1
          className={styles.heroTitle}
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
        >
          <span className={styles.heroLine}>Mapping peatland condition at scale,</span>
          <span className={styles.heroLine}>one pixel at a time.</span>
        </motion.h1>

        <motion.p
          className={styles.heroSlogan}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.5 }}
        >
          Ireland&apos;s peatlands are degraded and under pressure. Now you can
          see where, understand how, and gain insights to know what to do.
        </motion.p>

        <motion.p
          className={styles.heroSubtitle}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.65 }}
        >
          AI2Peat deploys PeatSense, a remote sensing and AI-powered peatland condition
          platform that aims to deliver reliable and actionable environmental intelligence at any
          scale, anywhere, at any time. PeatSense&apos;s assessments of condition are aligned to the{" "}
          <span className={styles.heroHighlight}>Peatland Finance Ireland&apos;s standard for Ireland.</span>
        </motion.p>

        <motion.div
          className={styles.heroButtons}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.8 }}
        >
          {!authenticated && (
            <Link href="/login" className={styles.heroBtnPrimary}> Sign up</Link>
          )}
          <Link href="/peatsense" className={styles.heroBtnPrimary}> Explore our services</Link>
          <Link href="/demo" className={styles.heroBtnSecondary}> Try the live demo</Link>
        </motion.div>

      </section>

      <SponsorLogos />

      <section className={`${styles.peatlandsSection} ${fraunces.variable}`}>
        <div className={styles.peatlandsGrid}>
          <div className={styles.peatlandsLabel}>About peatlands</div>
          <div>
            <p className={styles.peatlandsBig}>
              Peatlands are wetland ecosystems that provide a range of important ecosystem
              services to society. They cover 3% of the land on Earth on all continents,
              yet they store double the carbon stored by all forests combined : 20% of
              surface land area.{" "}
              <DarkenText>
                Peatlands also regulate water resources by storing and releasing it slowly,
                which helps to mitigate drought and flooding. Furthermore, peatlands support
                many rare and specially adapted flora, such as the insect-eating Sundew, or
                fauna, such as the sky-dancing Hen Harrier. Peatland biodiversity underpins
                not only global climate systems but also local processes such as water quality
                and pollination.
              </DarkenText>
            </p>

            <p className={styles.peatlandsBig} style={{ marginTop: "2.5rem" }}>
              About 23% of Ireland is covered by peat soil, yet it is estimated that at least 80% of
              peatlands are in bad condition.{" "}
              <DarkenText>
                The related negative impacts - environmental, social and financial - are now
                a major challenge.
              </DarkenText>
            </p>

            <p className={styles.peatlandsCta}>
              Continuously knowing the condition of our peatlands is the necessary requirement
              towards restoring them.{" "}
              <DarkenText>
                AI2Peat is here to make this available to anyone.
              </DarkenText>
            </p>
          </div>
        </div>
      </section>

      <section className={styles.whatSection}>
        <div>
          <motion.h2
            className={styles.whatTitle}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            What we do
          </motion.h2>

          <motion.p
            className={styles.whatText}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.1 }}
          >
            We combine remote sensing, artificial intelligence and ecological expertise
            to infer aspects of peatland condition at any scale : from local site to
            national level, from weekly updates to seasonal to multi-year.
          </motion.p>

          <motion.p
            className={styles.whatText}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.2 }}
          >
            We provide bespoke assessment at a click, putting the power of remote
            sensing, AI and ecological expertise directly in the hands of the people
            who need it most.
          </motion.p>

          <motion.blockquote
            className={styles.whatQuote}
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.3 }}
          >
            &ldquo;Mapping peatland condition at scale, one pixel at a time.&rdquo;
          </motion.blockquote>

          <motion.div
            className={styles.whatButtons}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.38 }}
          >
            <Link href="/demo" className={styles.btnOutline}>Try the live demo</Link>
            <Link href="/contact-us" className={styles.btnGreen}>Contact us</Link>
          </motion.div>

          <div className={styles.whatStats}>
            {[
              { value: 800, suffix: " km²", label: "of peatlands mapped through time" },
              { value: 10, suffix: "", label: "AI-driven condition assessment types" },
              { value: 3, suffix: "", label: "on-demand services" },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                className={styles.whatStat}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.5 }}
                transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.45 + i * 0.1 }}
              >
                <span className={styles.whatStatNum}>
                  <Counter value={stat.value} suffix={stat.suffix} />
                </span>
                <span className={styles.whatStatLabel}>{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.75, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.15 }}
        >
          <Image
            src="/what-we-dp.jpg"
            alt="What we do — remote sensing, AI and ecological expertise"
            width={0}
            height={0}
            sizes="100vw"
            className={styles.whatWeDoImg}
          />
        </motion.div>
      </section>

      <OurServices />

      <section className={styles.whoSection}>
        <Reveal>
          <div className={styles.sectionLabel}>Who it is for</div>
          <h2 className={styles.whoTitle}>Built for everyone who cares about peatlands</h2>
          <p className={styles.whoIntro}>
            From national agencies to individual ecologists, AI2Peat gives every stakeholder
            the intelligence they need at the scale they work at.
          </p>
        </Reveal>
        <div className={styles.whoGrid}>
          {[
            {
              tag: "Conservation",
              title: "Conservation bodies & NGOs",
              desc: "Identify arising pressures and monitor restoration progress at scale — without waiting for costly fieldwork campaigns.",
            },
            {
              tag: "Government",
              title: "Government & regulators",
              desc: "Meet obligations under the EU Habitats Directive, the Nature Restoration Law, and the UN Paris Agreement with reproducible, auditable spatial data.",
            },
            {
              tag: "Research",
              title: "Researchers",
              desc: "Access reproducible, science-backed spatial data aligned to recognised standards for publication and cross-study comparison.",
            },
            {
              tag: "Ecology",
              title: "Ecologists",
              desc: "Strategize and plan your work based on our quick scan at scale — prioritise field visits where they matter most.",
            },
            {
              tag: "Finance",
              title: "Carbon farming & nature capital investors",
              desc: "Quantify restoration and GHG sequestration potential, and de-risk investment in peatland assets with independent remote-sensing evidence.",
            },
            {
              tag: "Infrastructure",
              title: "Wind energy & infrastructure developers",
              desc: "Get insight into peatland condition before deciding your actions — understand baseline risk early and reduce project delays.",
            },
          ].map((w, i) => (
            <Reveal key={w.title} delay={i * 0.07} className={styles.whoCard}>
              <span className={styles.whoTag}>{w.tag}</span>
              <h3 className={styles.whoCardTitle}>{w.title}</h3>
              <p className={styles.whoCardDesc}>{w.desc}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── OUR VALUES ── */}
      <section className={`${styles.valuesSection} ${fraunces.variable}`}>
        {/* Shane's quote */}
        <div className={styles.shaneGrid}>
          <div className={styles.shaneAttribution}>
            <div className={styles.shaneBy}>Dr. Shane Regan</div>
            <div className={styles.shanePortrait} aria-hidden="true">SR</div>
          </div>
          <blockquote className={styles.shaneQuote}>
            <p>
              &ldquo;The problem to be addressed really is reducing CO<sub>2</sub> emissions
              from degraded peatlands by rewetting them.{" "}
              <DarkenText>
                We need to know where this is possible and how feasible that is.
                A national peatland condition map will help this, so NPWS and others
                can better identify areas to manage and restore.&rdquo;
              </DarkenText>
            </p>
            <footer className={styles.shaneMeta}>
              Dr. Shane Regan &mdash; NPWS, AI2Peat&rsquo;s Societal Impact Champion
            </footer>
          </blockquote>
        </div>

        <Reveal>
          <div className={styles.sectionLabel}>Our values</div>
        </Reveal>
        <Reveal>
          <h2 className={styles.valuesTitle}>What drives every decision we make</h2>
        </Reveal>
        <div className={styles.valuesGrid}>
          {[
            {
              num: "01",
              title: "Nature is our main stakeholder",
              desc: "Every decision we make is measured against one question: will Nature benefit from it?",
            },
            {
              num: "02",
              title: "Collaboration is our greatest force multiplier",
              desc: "No single organisation or dataset can solve the peatland crisis alone. We build with our partners, not beside them.",
            },
            {
              num: "03",
              title: "Our AI models work with humans, not instead of them",
              desc: "Our models amplify ecological expertise — they never replace it. Science leads; AI accelerates.",
            },
          ].map((v, i) => (
            <Reveal key={v.num} delay={i * 0.1} className={styles.valueItem}>
              <span className={styles.valueNum}>{v.num}</span>
              <h3 className={styles.valueTitle}>{v.title}</h3>
              <p className={styles.valueDesc}>{v.desc}</p>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <Link href="/about-us" className={styles.btnGreen} style={{ marginTop: 40, display: "inline-block" }}>
             About us
          </Link>
        </Reveal>
      </section>

      {/* ── ACCOLADES ── */}
      <section className={styles.accoladesSection}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          <div className={styles.sectionLabel}>Recognition</div>
        </motion.div>
        <motion.h2
          className={styles.accoladesTitle}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.1 }}
        >
          Recognised for impact
        </motion.h2>
        <motion.div
          className={styles.recognitionWrapper}
          initial={{ opacity: 0, y: 36, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.18 }}
        >
          <Recognition />
        </motion.div>
      </section>

      {/* ── GET IN TOUCH ── */}
      <section className={styles.ctaSection}>
        <div>
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <div className={styles.sectionLabel} style={{ color: "#276749", marginBottom: 14 }}>Say hello</div>
          </motion.div>
          <motion.h2
            className={styles.ctaTitle}
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.65, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.08 }}
          >
            Get in touch
          </motion.h2>
          <motion.p
            className={styles.ctaText}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.18 }}
          >
            Whether you&apos;re scoping a restoration project, shaping a national
            monitoring strategy, or looking to integrate peatland data into your
            workflows : we&apos;re here to help. Let&apos;s explore how AI2Peat
            can support your goals with speed, accuracy, and clarity.
          </motion.p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.15 }}
        >
          <div className={styles.ctaForm}>
            {["Your name", "Email address", "Organisation"].map((placeholder, i) => (
              <motion.input
                key={placeholder}
                type={placeholder === "Email address" ? "email" : "text"}
                placeholder={placeholder}
                className={styles.ctaInput}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.28 + i * 0.08 }}
              />
            ))}
            <motion.textarea
              placeholder="How can we help?"
              className={styles.ctaTextarea}
              rows={4}
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.52 }}
            />
            <motion.button
              className={styles.ctaSubmit}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.62 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              ↗ Send message
            </motion.button>
          </div>
        </motion.div>
      </section>
      <Footer />
    </div>
  );
}
